import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { CustomFieldDef, CreateCustomFieldInput } from './types';

const STORAGE_KEY = 'helios_custom_fields';

const INITIAL_FALLBACK_FIELDS: CustomFieldDef[] = [
  {
    id: 'cf-1',
    entity_type: 'contact',
    field_key: 'linkedin_url',
    field_type: 'text',
    label: 'LinkedIn Profile',
    required: false,
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-2',
    entity_type: 'company',
    field_key: 'annual_revenue_bracket',
    field_type: 'select',
    label: 'Annual Revenue Bracket',
    required: true,
    options: ['<$1M', '$1M-$10M', '$10M-$50M', '$50M+'],
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-3',
    entity_type: 'contact',
    field_key: 'preferred_language',
    field_type: 'select',
    label: 'Preferred Language',
    required: false,
    options: ['English', 'Spanish', 'French', 'German', 'Japanese'],
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-4',
    entity_type: 'partner_tier',
    field_key: 'discount_percentage',
    field_type: 'number',
    label: 'Discount Rate (%)',
    required: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-5',
    entity_type: 'partner_tier',
    field_key: 'certification_level',
    field_type: 'select',
    label: 'Certification Level',
    required: true,
    options: ['Silver Partner', 'Gold Partner', 'Platinum Partner'],
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-6',
    entity_type: 'it_asset',
    field_key: 'serial_number',
    field_type: 'text',
    label: 'Hardware Serial Number',
    required: true,
    created_at: new Date().toISOString(),
  },
  {
    id: 'cf-7',
    entity_type: 'it_asset',
    field_key: 'warranty_expiry',
    field_type: 'date',
    label: 'Warranty Expiry Date',
    required: false,
    created_at: new Date().toISOString(),
  },
];

function getStoredFields(): CustomFieldDef[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return INITIAL_FALLBACK_FIELDS;
}

export function useCustomFields() {
  const [fields, setFields] = useState<CustomFieldDef[]>(getStoredFields);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persist = useCallback((next: CustomFieldDef[]) => {
    setFields(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }, []);

  const fetchFields = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await coreApi.get<CustomFieldDef[]>('/custom-fields');
      if (Array.isArray(data) && data.length > 0) {
        const current = getStoredFields();
        const apiIds = new Set(data.map((d) => d.id));
        const merged = [...data, ...current.filter((c) => !apiIds.has(c.id))];
        persist(merged);
      }
    } catch {
      // use local
    } finally {
      setIsLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const createField = async (input: CreateCustomFieldInput) => {
    const newField: CustomFieldDef = {
      ...input,
      id: `cf-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    persist([newField, ...fields]);
    try {
      const created = await coreApi.post<CustomFieldDef>('/custom-fields', input);
      if (created && created.id) {
        persist([created, ...fields]);
      }
    } catch {
      // already saved locally
    }
  };

  const updateField = async (id: string, input: Partial<CustomFieldDef>) => {
    const next = fields.map((f) => (f.id === id ? { ...f, ...input } : f));
    persist(next);
    try {
      await coreApi.put<CustomFieldDef>(`/custom-fields/${id}`, input);
    } catch {
      // ignore
    }
  };

  const deleteField = async (id: string) => {
    const next = fields.filter((f) => f.id !== id);
    persist(next);
    try {
      await coreApi.delete(`/custom-fields/${id}`);
    } catch {
      // ignore
    }
  };

  return {
    fields,
    isLoading,
    error,
    createField,
    updateField,
    deleteField,
    reload: fetchFields,
  };
}

export default useCustomFields;
